import React from "react";
import { api } from "@/lib/api";

/**
 * Live preview of how the Sign-Off Binder cover would render given the
 * current Company Settings + theme + logo. Pure presentational — no fetches.
 *
 * R3 (Feb 2026 close-out): when Company Settings are incomplete, the preview
 * MUST render an explicit "Settings Incomplete" warning instead of a silent
 * placeholder ("Your Company") that could be mistaken for persisted data.
 */
export function BinderCoverPreview({ settings, logoAssetId }) {
  const t = settings.brand_theme || {};
  const primary = t.primary_color || "#1f5e3a";
  const secondary = t.secondary_color || "#fbfaf7";
  const accent = t.accent_color || "#1a1a1a";
  const textOnPrimary = t.text_on_primary || "#ffffff";
  const logoSrc = logoAssetId ? api.assetUrl(logoAssetId) : null;

  // Identify missing identity fields — exactly the set the Cert Package
  // / Signed Binder need to render correctly.
  const c = settings.contact || {};
  const missing = [];
  if (!(settings.company_name || "").trim()) missing.push("Company Name");
  if (!(c.contact_name || "").trim()) missing.push("Preparer Name");
  if (!(c.contact_position || "").trim()) missing.push("Preparer Title");

  const companyName = (settings.company_name || "").trim();
  const legalName = (settings.legal_name || "").trim();

  // ── Settings-incomplete state ───────────────────────────────────────────
  if (missing.length > 0) {
    return (
      <div
        data-testid="binder-preview-incomplete"
        style={{
          background: "#fff8e1",
          border: "1px solid #f0d780",
          borderRadius: 8,
          padding: 18,
          minHeight: 260,
          boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
          display: "flex",
          flexDirection: "column",
          gap: 10,
          color: "#5b4400",
        }}
      >
        <div style={{ fontSize: 13, fontWeight: 700, letterSpacing: "0.02em" }}>
          ⚠ Company Settings Incomplete
        </div>
        <div style={{ fontSize: 12, lineHeight: 1.5 }}>
          Complete Company Settings to preview your production branding.
        </div>
        <div style={{ fontSize: 12 }}>
          <div style={{ fontWeight: 600, marginBottom: 4 }}>Missing:</div>
          <ul
            data-testid="binder-preview-missing-list"
            style={{ margin: 0, paddingLeft: 18, lineHeight: 1.5 }}
          >
            {missing.map((m) => (
              <li key={m} data-testid={`binder-preview-missing-${m.replace(/\s+/g, "-").toLowerCase()}`}>
                {m}
              </li>
            ))}
          </ul>
        </div>
        <div style={{ fontSize: 11, opacity: 0.7, marginTop: "auto" }}>
          No placeholder names will appear on the actual Certification Package
          or Signed Binder — those fields will simply be blank until populated.
        </div>
      </div>
    );
  }

  // ── Fully-populated preview ─────────────────────────────────────────────
  return (
    <div
      data-testid="binder-preview"
      style={{
        background: secondary,
        border: `1px solid ${accent}22`,
        borderRadius: 8,
        padding: 18,
        minHeight: 260,
        boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        {logoSrc ? (
          <img src={logoSrc} alt="logo" style={{ height: 36, maxWidth: 120, objectFit: "contain" }} />
        ) : (
          <div
            style={{
              height: 36, width: 36, borderRadius: 6,
              background: primary, color: textOnPrimary,
              display: "grid", placeItems: "center",
              fontWeight: 700, fontSize: 14,
            }}
          >
            {companyName[0].toUpperCase()}
          </div>
        )}
        <div>
          <div style={{ fontWeight: 700, color: accent, fontSize: 16 }}>{companyName}</div>
          {legalName && <div style={{ color: accent, opacity: 0.55, fontSize: 12 }}>{legalName}</div>}
        </div>
      </div>

      <div>
        <div style={{
          color: accent, fontSize: 22, fontWeight: 700, marginBottom: 4,
        }}>
          Certification Package
        </div>
        <div style={{ color: accent, opacity: 0.7, fontSize: 13 }}>April 2026 · BMO Operating</div>
      </div>

      <div style={{
        background: primary, color: textOnPrimary,
        padding: "10px 14px", borderRadius: 6, fontSize: 12, fontWeight: 600,
        letterSpacing: 0.4,
      }}>
        OFFICE OF THE CONTROLLER · INTERNAL MANAGEMENT CERTIFICATION
      </div>
    </div>
  );
}
